import React from 'react';
import { cx } from './cx';

export type BadgeTone = 'neutral' | 'primary' | 'accent' | 'success' | 'warning' | 'danger' | 'info';

interface BadgeProps {
  tone?: BadgeTone;
  /** Solid fill for the single most important status on a card (e.g. TRIAL BOOKED). */
  solid?: boolean;
  children: React.ReactNode;
  className?: string;
}

const Badge: React.FC<BadgeProps> = ({ tone = 'neutral', solid, children, className }) => (
  <span className={cx('ds-badge', `ds-badge--${tone}`, solid && 'ds-badge--solid', className)}>{children}</span>
);

/**
 * One mapping from backend status strings to a tone, so the same status looks the
 * same on every page. Add new statuses here, not in pages.
 */
const STATUS_TONES: Record<string, BadgeTone> = {
  pending: 'warning',
  approved: 'success',
  success: 'success',
  successful: 'success',
  completed: 'success',
  active: 'success',
  rejected: 'danger',
  failed: 'danger',
  cancelled: 'neutral',
  expired: 'neutral',
  draft: 'neutral',
  processing: 'info',
  scheduled: 'info',
  // Teacher hiring (artium-teacher-hiring docs/API.md)
  awaiting_review: 'accent',
  in_progress: 'info',
  queued: 'info',
  dialing: 'info',
  invited: 'info',
  no_answer: 'warning',
  no_show: 'warning',
  sent: 'success',
  skipped: 'neutral',
  advance: 'success',
  review: 'warning',
  reject: 'danger',
  pass: 'success',
  hold: 'warning',
  fail: 'danger',
};

export const StatusBadge: React.FC<{ status?: string | null; label?: React.ReactNode }> = ({ status, label }) => {
  const key = String(status ?? '').trim().toLowerCase();
  return <Badge tone={STATUS_TONES[key] ?? 'neutral'}>{label ?? (key || 'unknown')}</Badge>;
};

export default Badge;
